<script setup lang="ts">
import { computed } from "vue";
import { useTranslation } from "i18next-vue";
import PhosphorIcon from "@/components/UI/PhosphorIcon.vue";
import DRBtn from "@/components/UI/DRBtn.vue";
import { classifyEntity } from "@/utils/plan-analysis/grouping";
import { usePlanAnalysisState } from "@/stores/PlanAnalysisState";
import type {
  MarkerPhotoState,
  PhotoAttachment,
  PlanListEntity,
} from "@/utils/plan-analysis/types";

/**
 * Une entité dans la liste dépliée d'une carte (groupe d'analyse ou
 * observation) : icône selon le rôle, libellé compact, retrait. Toucher la
 * ligne zoome et sélectionne l'entité. Une photo dont le rattachement a été
 * tranché sans certitude (temps, ambiguïté, arbitrage IA) porte un « ? » :
 * ses alternatives s'ouvrent dans une infobulle, un tap la déplace. Le
 * marqueur photo d'une observation dit s'il a sa photo.
 */

const props = defineProps<{
  entity: PlanListEntity;
  /** Rattachement photo de cette entité (marqueurs d'un groupe d'analyse). */
  attachment?: PhotoAttachment;
  /** Marqueur d'une observation : une photo l'illustre-t-elle ? */
  photoState?: MarkerPhotoState;
  /** Infobulle du bouton « Retirer » (par défaut : retirer du groupe). */
  removeTitle?: string;
}>();

defineEmits<{
  (e: "select"): void;
  (e: "remove"): void;
  (e: "move", targetGroupId: string): void;
}>();

const { t } = useTranslation();
const state = usePlanAnalysisState();

const role = computed(() => classifyEntity(props.entity));

/** Règles tranchées sans certitude : l'inspecteur confirme ou déplace. */
const UNCERTAIN_RULES = new Set(["temporal", "ambiguous", "ai"]);
const isUncertain = computed(
  () => !!props.attachment && UNCERTAIN_RULES.has(props.attachment.rule),
);
/** Alternatives encore existantes (un groupe corrigé change d'id). */
const alternatives = computed(() =>
  (props.attachment?.alternatives ?? []).filter(
    (a) => state.groupNumber(a.groupId) > 0,
  ),
);

const icon = computed(() => {
  if (role.value === "marker") return "camera";
  if (role.value === "annotation") return "text-aa";
  return "pencil-simple-line";
});

const label = computed(() => {
  if (role.value === "marker") {
    return t("canvas:analysis.entityList.photo", {
      number: props.entity.markerLabel ?? "?",
    });
  }
  if (role.value === "annotation") {
    return (
      props.entity.text?.trim() || t("canvas:analysis.entityList.emptyText")
    );
  }
  return props.entity.defectName ?? props.entity.kind;
});
</script>

<template>
  <div
    class="entity-item d-flex align-items-center gap-2"
    @click.stop="$emit('select')"
  >
    <PhosphorIcon :name="icon" :size="18" class="entity-icon" />
    <span class="entity-label text-truncate flex-grow-1">{{ label }}</span>
    <span
      v-if="photoState"
      class="photo-state d-inline-flex align-items-center flex-shrink-0"
      :class="photoState"
    >
      <PhosphorIcon
        :name="photoState === 'covered' ? 'check' : 'camera-slash'"
        :size="11"
        weight="bold"
      />
      {{ t(`canvas:observations.card.markerPhoto.${photoState}`) }}
    </span>
    <el-popover
      v-if="isUncertain"
      trigger="click"
      :width="250"
      placement="top-end"
    >
      <template #reference>
        <button
          type="button"
          class="uncertain-badge"
          :title="t('canvas:analysis.photo.uncertain')"
          @click.stop
        >
          <PhosphorIcon name="question" :size="12" weight="bold" />
        </button>
      </template>
      <div class="uncertain-popup" @click.stop>
        <div class="uncertain-title">
          {{ t("canvas:analysis.photo.uncertain") }}
        </div>
        <div class="uncertain-rule">
          {{ t(`canvas:analysis.photo.rule.${attachment?.rule}`) }}
        </div>
        <div
          v-for="alt in alternatives"
          :key="alt.groupId"
          class="uncertain-alt d-flex align-items-center gap-2"
        >
          <span class="flex-grow-1">
            {{
              t("canvas:analysis.photo.alternative", {
                number: state.groupNumber(alt.groupId),
              })
            }}
          </span>
          <DRBtn
            :label="t('canvas:analysis.photo.move')"
            variant="outline"
            size="xs"
            @click="$emit('move', alt.groupId)"
          />
        </div>
        <div v-if="alternatives.length === 0" class="uncertain-none">
          {{ t("canvas:analysis.photo.noAlternative") }}
        </div>
      </div>
    </el-popover>
    <DRBtn
      :label="t('canvas:analysis.entityList.removeShort')"
      variant="flat-danger"
      size="xs"
      :title="removeTitle ?? t('canvas:analysis.entityList.remove')"
      @click.stop="$emit('remove')"
    />
  </div>
</template>

<style lang="scss" scoped>
.entity-item {
  padding: 5px 6px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.84rem;

  &:hover {
    background: color-mix(
      in srgb,
      var(--card-accent, var(--dr-report)) 14%,
      transparent
    );
  }
}

.entity-icon {
  opacity: 0.7;
  flex-shrink: 0;
}

.entity-label {
  min-width: 0;
}

// État photo d'un marqueur : vert = une photo l'illustre, ambre = à faire.
.photo-state {
  gap: 3px;
  border-radius: 10px;
  padding: 1px 7px;
  font-size: 0.68rem;
  font-weight: 600;
  white-space: nowrap;

  &.covered {
    background-color: color-mix(in srgb, var(--bs-success) 20%, transparent);
    color: color-mix(in srgb, var(--bs-success) 60%, white);
  }

  &.missing {
    background-color: color-mix(in srgb, var(--bs-warning) 20%, transparent);
    color: var(--bs-warning);
  }
}

.uncertain-badge {
  border: none;
  border-radius: 50%;
  width: 18px;
  height: 18px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--bs-warning);
  color: #1a1400;
  cursor: pointer;
  flex-shrink: 0;
}

.uncertain-title {
  font-weight: 600;
  font-size: 0.78rem;
  color: var(--bs-warning);
  margin-bottom: 2px;
}

.uncertain-rule {
  font-size: 0.72rem;
  opacity: 0.7;
  margin-bottom: 6px;
}

.uncertain-alt {
  font-size: 0.78rem;
  padding: 3px 0;
}

.uncertain-none {
  font-size: 0.74rem;
  opacity: 0.6;
}
</style>
